import { useEffect, useState, useRef } from 'react'
import { Edit, Trash2, Users } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { userService, UserProfile } from '../../core/services/user.service'
import { UserRole } from '../../core/enums'
import { Card } from '../../components/ui/Card'
import Input from '../../components/ui/Input'
import DeleteModal from '../../components/ui/DeleteModal'
import Modal from '../../components/ui/Modal'
import AddButton from '../../components/buttons/AddButton'
import toast from 'react-hot-toast'
import Badge from '../../components/ui/Badge'
import { getUserRoleLabel } from '../../core/i18n'

export default function UserManagementPage() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasBusinesses, setHasBusinesses] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    password: '',
    userRole: UserRole.BUSINESS_EMPLOYEE,
    isActive: true
  })

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadUsers = async () => {
    setIsLoading(true)
    try {
      const data = await userService.getAllUsers()
      setUsers(data)
      setHasBusinesses(true);
    } catch (error: any) {
      setHasBusinesses(false);
      console.error('Failed to load users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddClick = () => {
    setFormData({
      name: '',
      phoneNumber: '',
      email: '',
      password: '',
      userRole: UserRole.BUSINESS_EMPLOYEE,
      isActive: true
    })
    setShowAddModal(true)
  }

  const handleEditClick = (user: UserProfile) => {
    setSelectedUser(user)
    // Ensure isActive is properly loaded - preserve false values
    const isActive = typeof user.isActive === 'boolean' ? user.isActive : true
    setFormData({
      name: user.name || '',
      phoneNumber: user.phoneNumber || '',
      email: user.email || '',
      password: '',
      userRole: user.userRole as UserRole,
      isActive: isActive
    })
    setShowEditModal(true)
  }

  const handleDeleteClick = (user: UserProfile) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return

    setIsDeleting(true)
    try {
      await userService.deleteUser(selectedUser.id)
      toast.success(t('userDeleted'))
      setShowDeleteModal(false)
      setSelectedUser(null)
      await loadUsers()
    } catch (error: any) {
      console.error('Failed to delete user:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSave = async () => {
    if (!formData.name) {
      toast.error(t('nameRequired'))
      return
    }

    if (!formData.phoneNumber) {
      toast.error(t('phoneNumberRequired'))
      return
    }

    if (!formData.email) {
      toast.error(t('emailRequired'))
      return
    }

    if (showAddModal && !formData.password) {
      toast.error(t('passwordRequired'))
      return
    }

    setIsSaving(true)
    try {
      if (showAddModal) {
        await userService.createUser({
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          password: formData.password,
          userRole: formData.userRole
        })
        toast.success(t('userCreated'))
        setShowAddModal(false)
      } else if (showEditModal && selectedUser) {
        await userService.updateUser(selectedUser.id, {
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          password: formData.password || undefined,
          userRole: formData.userRole,
          isActive: formData.isActive
        })
        toast.success(t('userUpdated'))
        setShowEditModal(false)
        setSelectedUser(null)
      }
      setFormData({
        name: '',
        phoneNumber: '',
        email: '',
        password: '',
        userRole: UserRole.BUSINESS_EMPLOYEE,
        isActive: true
      })
      await loadUsers()
    } catch (error: any) {
      console.error('Failed to save user:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-gray-700 mt-1">{t('userManagementDescription')} ({users.length})</p>
        </div>
        {hasBusinesses && (
          <AddButton
            onClick={handleAddClick}
            title={t('addUser')}
          />
        )}
      </div>

      {/* Users List */}
      <Card className="overflow-hidden">
        {users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Users className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('noUsers')}
            </h3>
            <p className="text-gray-600 text-center">
              {t('noUsersDescription')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {t('user')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {t('contact')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {t('role')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-5 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.phoneNumber}</div>
                      {user.email && (
                        <div className="text-sm text-gray-500">{user.email}</div>
                      )}
                    </td>
                    <td className="px-5 py-2 whitespace-nowrap">
                      <Badge variant="gray" size="sm">
                        {getUserRoleLabel(user.userRole)}
                      </Badge>
                    </td>
                    <td className="px-5 py-2 whitespace-nowrap">
                      <Badge variant={user.isActive ? 'success' : 'danger'} size="sm">
                        {user.isActive ? t('active') : t('inactive')}
                      </Badge>
                    </td>
                    <td className="px-5 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                          title={t('edit')}
                        >
                          <Edit className="w-4 h-4 text-gray-700" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="p-1.5 hover:bg-red-100 rounded transition-colors"
                          title={t('delete')}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('addUser')}
        actionButtonText={t('create')}
        onAction={handleSave}
        actionButtonLoading={isSaving}
        actionButtonDisabled={isSaving}
        actionButtonClassName="text-sm h-10 py-2 px-4"
        cancelButtonClassName="border-gray-300 text-[#1E293B] hover:text-[#1E293B] hover:border-[#1E293B] hover:bg-gray-50 text-sm h-10 py-2 px-4"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('name')} *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('enterName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('phoneNumber')} *
            </label>
            <Input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder={t('enterPhoneNumber')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('email')} *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('enterEmail')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('password')} *
            </label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={t('enterPassword')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('role')} *
            </label>
            <select
              value={formData.userRole}
              onChange={(e) => setFormData({ ...formData, userRole: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={UserRole.BUSINESS_EMPLOYEE}>{getUserRoleLabel(UserRole.BUSINESS_EMPLOYEE)}</option>
              <option value={UserRole.BUSINESS_CASHIER}>{getUserRoleLabel(UserRole.BUSINESS_CASHIER)}</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedUser(null)
        }}
        title={t('editUser')}
        actionButtonText={t('save')}
        onAction={handleSave}
        actionButtonLoading={isSaving}
        actionButtonDisabled={isSaving}
        actionButtonClassName="text-sm h-10 py-2 px-4"
        cancelButtonClassName="border-gray-300 text-[#1E293B] hover:text-[#1E293B] hover:border-[#1E293B] hover:bg-gray-50 text-sm h-10 py-2 px-4"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('name')} *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('enterName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('phoneNumber')} *
            </label>
            <Input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder={t('enterPhoneNumber')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('email')} *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('enterEmail')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('password')} ({t('leaveEmptyToKeepCurrent')})
            </label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={t('enterNewPassword')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('role')} *
            </label>
            <select
              value={formData.userRole}
              onChange={(e) => setFormData({ ...formData, userRole: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={UserRole.BUSINESS_EMPLOYEE}>{getUserRoleLabel(UserRole.BUSINESS_EMPLOYEE)}</option>
              <option value={UserRole.BUSINESS_CASHIER}>{getUserRoleLabel(UserRole.BUSINESS_CASHIER)}</option>
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              />
              <div className="flex-1">
                <label htmlFor="isActive" className="text-sm font-medium text-gray-900 cursor-pointer">
                  {t('active')}
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formData.isActive
                    ? t('userActiveDescription')
                    : t('userInactiveDescription')
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedUser(null)
        }}
        onConfirm={handleDeleteConfirm}
        title={t('deleteUser')}
        message={selectedUser
          ? `${t('confirmDeleteUser')} "${selectedUser.name}"\n${t('thisActionCannotBeUndone')}`
          : t('confirmDeleteUser')
        }
        isLoading={isDeleting}
      />
    </div>
  )
}
